import { useEffect, useState } from "react";
import { Clock, ExternalLink, MapPin, Phone } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { INFO } from "@/data/content";
import { getStatus, WEEK } from "@/utils/hours";

export const Contatti = () => {
  const [status, setStatus] = useState(() => getStatus());

  useEffect(() => {
    const t = setInterval(() => setStatus(getStatus()), 60_000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="contatti"
      className="scroll-mt-20 bg-[#F5F5F5] py-20 text-espresso lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal>
          <Eyebrow light>Contatti</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            Vieni a trovarci!
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Reveal>
                <a
                  href={INFO.mapsDirections}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-link"
                  className="group block h-full rounded-2xl border border-espresso/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(20,16,14,0.12)]"
                >
                  <MapPin className="size-6 text-terra" />
                  <h3 className="mt-4 font-semibold">Indirizzo</h3>
                  <p className="mt-1 text-sm leading-relaxed text-espresso/60">
                    {INFO.address}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-terra">
                    Indicazioni
                    <ExternalLink className="size-3.5" />
                  </span>
                </a>
              </Reveal>

              <Reveal delay={0.08}>
                <a
                  href={INFO.phoneHref}
                  data-testid="contact-phone-link"
                  className="group block h-full rounded-2xl border border-espresso/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(20,16,14,0.12)]"
                >
                  <Phone className="size-6 text-terra" />
                  <h3 className="mt-4 font-semibold">Telefono</h3>
                  <p className="mt-1 text-sm text-espresso/60">
                    Prenota o chiedi informazioni
                  </p>
                  <span className="mt-4 inline-block font-mono text-lg font-semibold text-terra">
                    {INFO.phoneDisplay}
                  </span>
                </a>
              </Reveal>
            </div>

            <Reveal delay={0.14}>
              <div className="mt-4 rounded-2xl border border-espresso/10 bg-white p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="inline-flex items-center gap-2 font-semibold">
                    <Clock className="size-5 text-terra" />
                    Orari di apertura
                  </h3>
                  <span
                    data-testid="contact-status-badge"
                    className="inline-flex items-center gap-2 rounded-full border border-espresso/10 px-3 py-1.5 text-xs font-medium"
                  >
                    <span
                      className={`size-1.5 rounded-full ${
                        status.open ? "bg-[#2ECC71]" : "bg-[#E74C3C]"
                      }`}
                    />
                    {status.text}
                  </span>
                </div>
                <ul className="mt-5 divide-y divide-espresso/5">
                  {WEEK.map((d, i) => {
                    const today = i === status.idx;
                    return (
                      <li
                        key={d.key}
                        data-testid={`hours-row-${d.key}`}
                        className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm ${
                          today ? "bg-terra/10 font-semibold text-terra" : "text-espresso/70"
                        }`}
                      >
                        <span className="inline-flex items-center gap-2">
                          {d.label}
                          {today && (
                            <span className="rounded-full bg-terra px-2 py-0.5 text-[10px] uppercase tracking-wider text-cream">
                              Oggi
                            </span>
                          )}
                        </span>
                        <span className="font-mono">
                          {d.closed ? "Chiuso" : `${d.open} – ${d.close}`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="h-full">
            <div className="h-full min-h-[380px] overflow-hidden rounded-2xl border border-espresso/10 shadow-[0_14px_40px_rgba(20,16,14,0.10)] lg:min-h-[560px]">
              <iframe
                title="Mappa — Bar Incontro, Via Palmiro Togliatti 19, Pesaro"
                src={INFO.mapsEmbed}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map-iframe"
                className="h-full min-h-[380px] w-full border-0 lg:min-h-[560px]"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
